<template>
  <div class="inappropriate-behavior-form__container">
    <ValidationObserver ref="inappropriateBehaviorForm" v-slot="{ validated }">
      <div class="inappropriate-behavior-form-row-data__container">
        <ValidationProvider v-slot="{ errors }" rules="required">
          <CustomDropDownDetailed
            v-model="model.behaviorType"
            placeholder="Seleccione el tipo de conducta"
            :options="behaviorTypeOptions"
            :has-error="validated && !!errors[0]"
            error-msg="Favor seleccionar el tipo de conducta."
          />
        </ValidationProvider>
      </div>

      <div class="inappropriate-behavior-form-row-data__container">
        <SectionTitle content="Detonante" />
        <ValidationProvider v-slot="{ errors }" rules="required">
          <CustomTextArea
            v-model="model.trigger"
            show-char-count
            :maxlength="10000"
            :error="errors[0]"
            :count-classes="['d-block text-right']"
            label="¿Cuál es el detonante de esta conducta?"
            custom-error-msg="Favor describa el detonante."
          />
        </ValidationProvider>
      </div>

      <div class="inappropriate-behavior-form-row-data__container">
        <SectionTitle content="Ambiente" />
        <ValidationProvider v-slot="{ errors }" rules="required">
          <CustomTextArea
            v-model="model.environment"
            show-char-count
            :maxlength="10000"
            :error="errors[0]"
            :count-classes="['d-block text-right']"
            label="¿Describa en que tipo de ambiente es el más probable que este comportamiento ocurra?"
            custom-error-msg="Favor describa el ambiente."
          />
        </ValidationProvider>
      </div>

      <div class="inappropriate-behavior-form-row-data__container">
        <SectionTitle content="Función del comportamiento" />
        <p>
          ¿Qué debemos observar inmediatamente después de una conducta para
          entender por qué el estudiante la realiza?
        </p>

        <CustomCheckbox
          v-for="item in behavioralFunctionOptions"
          :key="item.id"
          class="mb-3"
          :value="model.behavioralFunctions.includes(item.id)"
          :text="item.name"
          @input="(checked) => handleBehavioralToggle(item.id, checked)"
        />

        <CustomError
          v-if="validated && !model.behavioralFunctions.length"
          message="Favor de seleccionar al menos una función del comportamiento."
          variant="no-border mt-1"
        />

        <div v-if="showBehavioralFunctionOtherTextField">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <CustomInput
              v-model="model.behavioralFunctionOtherText"
              type="text"
              :maxlength="'500'"
              :error="errors[0]"
              custom-error-msg="Campo requerido."
            />
          </ValidationProvider>
        </div>
      </div>

      <div class="inappropriate-behavior-form-row-data__container">
        <SectionTitle content="Plan Conductual" />
        <p>
          ¿Estas conductas requieren de un plan conductual por parte del EIS o
          algún especialista?
        </p>

        <ValidationProvider v-slot="{ errors }" rules="required">
          <CustomRadio
            v-model="model.behavioralPlan"
            name="behavioralPlan"
            class="row"
            :is-inline="false"
            :options="behavioralPlanOptions"
            :error="errors[0]"
            custom-error-msg="Favor de documentar el plan conductual."
          />
        </ValidationProvider>

        <div v-if="showBehavioralPlanFile" class="mt-2">
          <p style="margin-bottom: 5px">Subir Documento:</p>
          <ValidationProvider
            v-slot="{ errors }"
            :rules="!model.behavioralPlanFileId ? 'required' : ''"
          >
            <div class="inappropriate-behavior-form-file-input__container">
              <CustomFileInput
                ref="fileUpload"
                v-model="model.behavioralPlanFile"
                display-style="normal"
                :attachment-id="model.behavioralPlanFileId"
                :name="model.behavioralPlanFileName"
                :accept="'.pdf'"
                :error="errors[0]"
              />
            </div>
            <CustomError
              v-if="validated && !!errors[0]"
              message="Favor de anejar documento."
              variant="no-border mt-1"
            />
          </ValidationProvider>
        </div>
      </div>

      <div class="inappropriate-behavior-form-row-data__container">
        <SectionTitle content="Estrategias" />
        <ValidationProvider v-slot="{ errors }" rules="required">
          <IndexableGroup
            v-model="model.strategies"
            title="Estrategia"
            action-name="Añadir Estrategia"
            :has-error="validated && !!errors[0]"
            errorMsg="Favor de documentar al menos una estrategia."
          />
        </ValidationProvider>
      </div>

      <div class="inappropriate-behavior-form-row-data__container">
        <SectionTitle content="Colaboración Familiar" />
        <ValidationProvider v-slot="{ errors }" rules="required">
          <IndexableGroup
            v-model="model.familyCollaborations"
            title="Colaboración Familiar"
            action-name="Añadir Colaboración"
            :has-error="validated && !!errors[0]"
            errorMsg="Favor de documentar al menos una colaboración familiar."
          />
        </ValidationProvider>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import CustomCheckbox from 'CheckBox';
import CustomError from 'custom-error';
import SectionTitle from 'SectionTitle';
import CustomTextArea from 'CustomTextArea';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import CustomInput from '@/components/common/CustomInput';
import CustomRadio from '@/components/common/CustomRadio';
import IndexableGroup from '../Indexable/IndexableGroup.vue';
import CustomDropDownDetailed from './CustomDropDown.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

const BehavioralFunctionOtherId = 5;
const BehavioralPlanYes = 1;

export const GetDefaultInappropriateBehavior = () =>
  JSON.parse(
    JSON.stringify({
      id: null,
      behaviorType: null,
      trigger: '',
      environment: '',
      behavioralFunctions: [],
      behavioralFunctionOtherText: '',
      behavioralPlan: null,
      behavioralPlanFile: null,
      behavioralPlanFileId: null,
      behavioralPlanFileName: null,
      strategies: [],
      familyCollaborations: [],
    })
  );

export default {
  name: 'InappropriateBehaviorFormV2',
  components: {
    IndexableGroup,
    CustomError,
    CustomRadio,
    CustomInput,
    SectionTitle,
    CustomDropDownDetailed,
    CustomCheckbox,
    CustomTextArea,
    CustomDropDown,
    CustomFileInput,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    value: {
      type: Object,
      required: true,
      default: () => GetDefaultInappropriateBehavior(),
    },
    behaviorTypeOptions: {
      type: Array,
      required: true,
      default: () => [],
    },
    behavioralFunctionOptions: {
      type: Array,
      required: true,
      default: () => [],
    },
    behavioralPlanOptions: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      model: GetDefaultInappropriateBehavior(),
    };
  },
  computed: {
    showBehavioralFunctionOtherTextField() {
      return this.model.behavioralFunctions
        .map(Number)
        .includes(BehavioralFunctionOtherId);
    },
    showBehavioralPlanFile() {
      return this.model.behavioralPlan == BehavioralPlanYes;
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(this.model)) {
          const { behavioralPlan, behavioralFunctions, ...rest } = newValue;
          Object.assign(this.model, rest);

          this.model.behavioralPlan = behavioralPlan?.id;
          this.model.behavioralFunctions = behavioralFunctions?.map(
            (x) => x.id
          );
        }
      },
    },
    model: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
          this.$emit('input', newValue);
        }
      },
    },
  },
  methods: {
    handleBehavioralToggle(itemId, checked) {
      const exists = this.model.behavioralFunctions.includes(itemId);
      if (checked && !exists) {
        this.model.behavioralFunctions.push(itemId);
      } else if (!checked && exists) {
        this.model.behavioralFunctions = this.model.behavioralFunctions.filter(
          (id) => id !== itemId
        );
      }
    },
    async validate() {
      return await this.$refs.inappropriateBehaviorForm.validate();
    },
  },
};
</script>

<style scoped>
.inappropriate-behavior-form__container {
  padding: 0 20px;
  background-color: #fafbfa;
}

.inappropriate-behavior-form-row-data__container {
  margin-top: 20px;
}

.inappropriate-behavior-form-file-input__container {
  padding: 20px;
  display: flex;
  border-radius: 5px;
  justify-content: center;
  background-color: white;
  border: 1px solid #c6cdcf;
}
</style>
